{/* TODO: Frank to finalize copy before launch — current text is a generic placeholder */}
import { PublicNav } from "@/components/website/PublicNav";
import { PublicFooter } from "@/components/website/PublicFooter";
import { PublicLayout } from "@/components/website/PublicLayout";
import type { ReactNode } from "react";
import { useAppSettings } from "@/hooks/useAppSettings";

function CookieName({ children }: { children: ReactNode }) {
  return <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{children}</code>;
}

export default function CookiesPage() {
  const { data: settings } = useAppSettings();
  const businessName = settings?.site_logo_text
    ? `${settings.site_logo_text} ${settings.site_logo_subtitle ?? ""}`.trim()
    : "Tristano's Catering";
  const email = settings?.business_email || "info@tristanoscatering.ca";
  const lastUpdated = "October 2026";

  return (
    <PublicLayout className="min-h-screen bg-background flex flex-col">
      <PublicNav />

      <main id="main-content" className="flex-1">
        {/* Hero */}
        <div className="bg-primary py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-logo font-bold text-primary-foreground">
              Cookie Policy
            </h1>
            <p className="mt-2 text-primary-foreground/80 text-sm font-secondary">
              Last updated: {lastUpdated}
            </p>
          </div>
        </div>

        {/* Body */}
        <article className="max-w-3xl mx-auto px-4 py-10 md:py-14 font-secondary text-foreground/90 space-y-6 leading-relaxed">
          <p>
            This Cookie Policy explains what cookies and similar technologies are, how{" "}
            {businessName} uses them on our website, and the choices you have.
          </p>

          <section className="space-y-3">
            <h2 className="text-xl md:text-2xl font-logo font-semibold text-foreground">
              1. What are cookies?
            </h2>
            <p>
              Cookies are small text files that a website places on your device when you
              visit. They&rsquo;re widely used to make sites work, make them work more
              efficiently, and provide information to site owners. &ldquo;Local storage&rdquo;
              and similar browser technologies work in a similar way and are covered by this
              policy.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl md:text-2xl font-logo font-semibold text-foreground">
              2. How we use cookies
            </h2>
            <p>We use cookies and similar technologies for these purposes:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Strictly necessary</strong>: needed for core features such as staff
                sign-in, keeping your session, and keeping your quote or cart in progress.
                These can&rsquo;t be turned off in our systems.
              </li>
              <li>
                <strong>Preferences</strong>: remember your cookie choice (the{" "}
                <CookieName>tc_consent</CookieName>{" "}
                cookie, shared across tristanoscatering.ca).
              </li>
              <li>
                <strong>Analytics</strong>: Google Analytics measures how visitors use the
                site so we can improve it. It sets{" "}
                <CookieName>_ga</CookieName> cookies.
              </li>
              <li>
                <strong>Advertising and measurement</strong>: the Meta (Facebook/Instagram)
                pixel sets <CookieName>_fbp</CookieName>{" "}
                and <CookieName>_fbc</CookieName>, and
                Google Ads conversion tracking sets{" "}
                <CookieName>_gcl_aw</CookieName>. They
                tell us whether our ads lead to enquiries. We also keep a record of the ad or
                campaign that brought you here (for example{" "}
                <CookieName>utm_source</CookieName> or{" "}
                <CookieName>fbclid</CookieName>) for up
                to 90 days. For your current visit we also note the website that sent you
                here and the first page you opened.
              </li>
            </ul>
            <p>
              Analytics and advertising cookies are on by default. Choose{" "}
              <strong>Decline</strong> on the cookie banner and we stop loading them and
              delete the campaign cookies we set ourselves. Cookies that Meta or Google have
              already set can be cleared in your browser settings.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl md:text-2xl font-logo font-semibold text-foreground">
              3. Your choices
            </h2>
            <p>
              Most browsers let you refuse or delete cookies from the browser settings. If you
              disable strictly necessary cookies, parts of the site (such as placing an order
              or signing in) may not work properly.
            </p>
            <p>
              The cookie banner lets you accept or decline analytics and advertising cookies.
              Your choice is saved in the <CookieName>tc_consent</CookieName>{" "}
              cookie and shared across tristanoscatering.ca, including our main website. To
              change it, clear your browser&rsquo;s cookies for tristanoscatering.ca and the
              banner will ask again.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl md:text-2xl font-logo font-semibold text-foreground">
              4. Changes to this policy
            </h2>
            <p>
              We may update this Cookie Policy from time to time to reflect changes in the
              technology we use or in the law. Please check back periodically.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl md:text-2xl font-logo font-semibold text-foreground">
              5. Contact
            </h2>
            <p>
              Questions about cookies? Email us at{" "}
              <a className="underline hover:text-primary" href={`mailto:${email}`}>
                {email}
              </a>
              .
            </p>
          </section>
        </article>
      </main>

      <PublicFooter />
    </PublicLayout>
  );
}
